import { useState, useEffect } from "react";
import { Dialog, DialogContent, IconButton, Typography, Box, Grid, Button, keyframes, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import Chart from "react-apexcharts";
import API from "../api";
import { dataGridStyle } from "../theme";
import CloseIcon from "@mui/icons-material/Close";
import CircularIndeterminate from "./circularProgress";
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';

const borderColorChange = keyframes`
  0% {
    border-color: #43B4B9;
  }
  33% {
    border-color: #596035;
  }
  66% {
    border-color: #562984;
  }
  100% {
    border-color: #43B4B9;
  }
`;

function DetailPanelContent({
  row,
  advancedVisualizer = false,
  tiresia
}) {
  const [logs, setLogs] = useState([]);
  const [, setLoading] = useState(false);

  useEffect(() => {
    setLogs([]);
    setLoading(true);
    const transformData = row.logs.map((item, index) => ({
      id: index,
      ...item,
    }));
    setLogs(transformData);
    setLoading(false);
  }, [row]);

  return (
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ color: "#43B4B9", fontWeight: "bold" }}>
              TIMESTAMP
            </TableCell>
            {tiresia ? (
              <>
                <TableCell sx={{ color: "#43B4B9", fontWeight: "bold" }}>
                  SOURCE PORT
                </TableCell>
                <TableCell sx={{ color: "#43B4B9", fontWeight: "bold" }}>
                  DESTINATION PORT
                </TableCell>
                <TableCell sx={{ color: "#43B4B9", fontWeight: "bold" }}>
                  ANOMALY SCORE
                </TableCell>
              </>
            ) : (
              <>
                <TableCell sx={{ color: "#43B4B9", fontWeight: "bold" }}>
                  SOURCE IP
                </TableCell>
                <TableCell sx={{ color: "#43B4B9", fontWeight: "bold" }}>
                  DESTINATION IP
                </TableCell>
              </>
            )}
          {advancedVisualizer && (
              <TableCell sx={{ color: "#43B4B9", fontWeight: "bold" }}>
                DETAIL
              </TableCell>
            )
          }
            
          </TableRow>
        </TableHead>
        <TableBody>
          {logs.map((item, index) => (
            <TableRow key={index}>
              <TableCell>{item.timeStamp}</TableCell>
              {tiresia ? (
                <>
                  <TableCell>{item.portaSorgente}</TableCell>
                  <TableCell>{item.portaDestinatario}</TableCell>
                  <TableCell>{item.score}</TableCell>
                </>
              ) : (
                <>
                  <TableCell>{item.ipAttaccante}</TableCell>
                  <TableCell>{item.ipDestinatario}</TableCell>
                </>
              )}
              {advancedVisualizer && (
                <TableCell>{item.dettaglio}</TableCell>
              )}
              
            </TableRow>
          ))}
        </TableBody>
      </Table>
  );
}

const HeatMapLog = ({
  logDetail,
  open,
  setOpen,
  advancedVisualizer = false,
}) => {
  const [series, setSeries] = useState([]);
  const [charged, setCharged] = useState(false);
  const [dataTable, setDataTable] = useState([]);
  const [chargedTable, setChargedTable] = useState(true);
  const [selectedDay, setSelectedDay] = useState("");
  const [selectedHour, setSelectedHour] = useState("");
  const [dialogTable, setDialogTable] = useState(false);
  const [tiresia, setTiresia] = useState(false);
  const [loadingTiresia, setLoadingTiresia] = useState(false);
  const [infoRow, setInfoRow] = useState({});
  const [viewInfo, setViewInfo] = useState(false);

  useEffect(() => {
    if (tiresia && logDetail.length > 0) {
      API.getHeatMapData({ dettaglio: logDetail, tiresia: true })
        .then((res) => {
          const series = Object.keys(res.data).map((day) => {
            return {
              name: day,
              data: Object.keys(res.data[day]).map((hour) => {
                return { x: `${hour}:00`, y: res.data[day][hour] };
              }),
            };
          });
          setSeries(series);
          setLoadingTiresia(false);
          //setCharged(true);
        })
        .catch((err) => {
          console.error('[HeatMapLog] Errore nel recupero della heatmap Tires-IA:', err);
          setLoadingTiresia(false);
          //setCharged(true);
        });
    } else if (logDetail.length > 0) {
      setSeries([]);
      setCharged(false);
      API.getHeatMapData({ dettaglio: logDetail })
        .then((res) => {
          const series = Object.keys(res.data).map((day) => {
            return {
              name: day,
              data: Object.keys(res.data[day]).map((hour) => {
                return { x: `${hour}:00`, y: res.data[day][hour] };
              }),
            };
          });
          setSeries(series);
          setCharged(true);
        })
        .catch((err) => {
          console.error('[HeatMapLog] Errore nel recupero della heatmap:', err);
          setCharged(true);
        });
    } else {
      setSeries([]);
      setCharged(true);
    }
  }, [logDetail, tiresia]);

  const TOSetTiresiaOnHeatMap = () => {
    if (tiresia) {
      setTiresia(false);
    } else {
      setLoadingTiresia(true);
      setTiresia(true);
    }
  };

  const options = {
    chart: {
      type: "heatmap",
      height: 350,
      events: {
        dataPointSelection: function (event, chartContext, config) {
          const hour = Array.from({ length: 24 }, (_, i) => `${i}:00`)[
            config.dataPointIndex
          ];
          const day = series[config.seriesIndex].name;
          setDataTable([]);
          setChargedTable(false);
          setSelectedDay(day);
          setSelectedHour(hour);
          API.getHeatMapData({
            dettaglio: logDetail,
            dayDetail: day,
            timeStampDetail: hour,
            ...(tiresia && { tiresia: tiresia }),
          })
            .then((res) => {
              const transformedData = res.data.map((item, index) => ({
                id: index,
                ...item,
              }));
              setDataTable(transformedData);
              setChargedTable(true);
              if (advancedVisualizer) {
                setDialogTable(true);
              }
            })
            .catch((err) => {
              console.error('[dataPointSelection] Errore nel recupero dei dettagli:', err);
              setChargedTable(true);
            });
        },
      },
    },
    plotOptions: {
      heatmap: {
        shadeIntensity: 0.5,
        colorScale: {
          ranges: [
            { from: 0, to: 0, color: "#2E2E2E", name: "No Data" },
            { from: 1, to: 10, color: "#58A8AF", name: "Low" },
            { from: 11, to: 100, color: "#C9B85E", name: "Medium" },
            { from: 101, to: 10000, color: "#F57F22", name: "High" },
            { from: 10001, to: Infinity, color: "#FF0000", name: "Very High" },
          ],
        },
      },
    },
    dataLabels: { enabled: false },
    xaxis: {
      type: "category",
      categories: Array.from({ length: 24 }, (_, i) => `${i}:00`),
    },
    title: {
      text: "Heatmap of log occurrences of the month grouped by day of the week",
      align: "center",
      style: { fontSize: "16px", color: "#eaeaea" },
    },
  };

  const columns = [
    {
      field: "count",
      headerName: "COUNT",
      minWidth: 50,
      flex: 1,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "ipAttaccante",
      headerName: "SOURCE",
      minWidth: 150,
      flex: 1,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "ipDestinatario",
      headerName: "DESTINATION",
      minWidth: 150,
      flex: 1,
      headerAlign: "left",
      align: "left",
    },
    {
      flex: 1,
      minWidth: 50,
      align: "center",
      renderCell: (params) => {
        return(
          <IconButton onClick={() => { setInfoRow(params.row); setViewInfo(true) }}>
              <InfoOutlineIcon color="#fff" fontSize="small"/>
          </IconButton>

        )
    }
    }
  ];

  /*const getDetailPanelHeight = useCallback(() => 300, []);

  const getDetailPanelContent = useCallback(
    ({ row }) => (
      <DetailPanelContent
        row={row}
        advancedVisualizer={advancedVisualizer}
        tiresia={tiresia}
      />
    ),
    [advancedVisualizer, tiresia]
  );*/

  const TOCloseDialogTable = () => {
    setDialogTable(false);
  };


  return advancedVisualizer ? (
    <div id="apexchart">
      {series.length > 0 ? (
        <Chart
          options={options}
          series={series}
          type="heatmap"
          height={600}
          id="chartGlobal"
        />
      ) : (
        <CircularIndeterminate charged={charged} title="No Data" />
      )}
      <Dialog
        open={dialogTable}
        sx={{
          "& .MuiDialog-paper": {
            width: "100%", // Imposta la larghezza della finestra di dialogo
            height: "100%", // Imposta l'altezza della finestra di dialogo
            maxWidth: "none", // Permette alla finestra di superare la larghezza massima predefinita
            backgroundColor: "4e4e4e",
            backgroundImage: "none",
          },
        }}
      >
        <IconButton
          onClick={TOCloseDialogTable}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ paddingTop: 100, width: "100%" }}>
          <Typography sx={{ mb: 5, fontSize: 18, fontWeight: 900 }}>
            Day: {selectedDay}; Hour: {selectedHour}
          </Typography>
          {dataTable.length > 0 ? (
            <Grid container spacing={2} sx={{minWidth: "80%", display: "flex", border: "solid 1px red"}}>
              <Grid size={6}>
                <Box sx={{display: "flex", justifyContent: "right", alignItems: "center"}}>
                  <DataGridPro
                    autoPageSize
                    pagination
                    rows={dataTable}
                    columns={columns}
                    //getDetailPanelContent={getDetailPanelContent}
                    //getDetailPanelHeight={getDetailPanelHeight}
                    sx={{...dataGridStyle,
                      borderRadius: 0,
                      "& .MuiDataGrid-footerContainer": {
                        borderTop: "none",
                        backgroundColor: "#449095",
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0,
                      }
                    }}
                  />
                </Box>
              </Grid>
              <Grid size={6}>
                <Box sx={{ p:2, border: "solid 2px #fff", borderRadius: "10px", height: "100%", width: "50%"}}>
                  {viewInfo ? 
                    <DetailPanelContent row={infoRow} tiresia={tiresia} advancedVisualizer={advancedVisualizer}/>
                    :
                    <Typography fontSize={20}>Click on info icon to view details</Typography>
                  }
                </Box>
              </Grid>
            </Grid>
          ) : (
            <CircularIndeterminate
              charged={chargedTable}
              title="No Data, click a point on HeatMap"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  ) : (
    <div id="apexchart">
      <Button
        variant={"contained"}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          backgroundColor: tiresia
            ? "#eaeaea !important"
            : "#2d2d2d !important",
          mb: 2,
          border: "2px solid", // aggiungi un bordo
          animation: `${borderColorChange} 3s linear infinite`,
          color: tiresia ? "#131C2D !important" : "white !important",
          "&:hover": {
            backgroundColor: "#eaeaea !important",
            color: "#131C2D !important",
          },
        }}
        style={{ fontWeight: "bold" }}
        onClick={TOSetTiresiaOnHeatMap}
      >
        Tires-IA{" "}
        {loadingTiresia && <CircularProgress size={25} color="inherit" />}
      </Button>
      {series.length > 0 ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Chart
            options={options}
            series={series}
            type="heatmap"
            height={600}
            width={1200}
            id="chartGlobal"
          />
          {selectedDay && selectedHour && (
            <Typography sx={{ mb: 5, fontSize: 18, fontWeight: 900 }}>
              Day: {selectedDay}; Hour: {selectedHour}
            </Typography>
          )}
          {dataTable.length > 0 ? (
            <Grid container spacing={2} sx={{width: "100%", display: "flex"}}>
              <Grid size={6} sx={{display: "flex", justifyContent: "right", alignItems: "center"}}>
                <Box sx={{width: "50%", height: "50vh"}}>
                  <DataGridPro
                    autoPageSize
                    pagination
                    rows={dataTable}
                    columns={columns}
                    //getDetailPanelContent={getDetailPanelContent}
                    //getDetailPanelHeight={getDetailPanelHeight}
                    sx={{...dataGridStyle, 
                      borderRadius: 0, 
                      "& .MuiDataGrid-footerContainer": {
                        borderTop: "none",
                        backgroundColor: "#449095",
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0,
                      }
                    }}
                  />
                  </Box>
              </Grid>
              <Grid size={6} sx={{display: "flex", justifyContent: "left", alignItems: "center"}}>
                <Box sx={{ 
                  p:2, 
                  border: "solid 2px #fff", 
                  borderRadius: "10px", 
                  height: "50vh", 
                  width: "80%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "top"
                }}>
                  {viewInfo ? 
                    <DetailPanelContent row={infoRow} tiresia={tiresia} advancedVisualizer={advancedVisualizer}/>
                    :
                    <Typography fontSize={20}>Click on info icon to view details</Typography>
                  }
                </Box>
              </Grid>
            </Grid>
          ) : (
            <CircularIndeterminate
              charged={chargedTable}
              title="No Data, click a point on HeatMap"
            />
          )}
        </div>
      ) : (
        <CircularIndeterminate charged={charged} title="No Data" />
      )}
    </div>
  );
};

export default HeatMapLog;
